import React, { useState, useEffect, useCallback } from "react";
import { IconPlus, IconX, IconEdit, IconTrash } from "@tabler/icons-react";
import MoonLoader from "react-spinners/MoonLoader";
import { useCRM } from "../CRMContext";
import {
  getAutomations,
  createAutomation,
  updateAutomation,
  deleteAutomation,
  toggleAutomation,
} from "../services/automations.service";
import { getTagsByPipeline } from "../../../../screens/Tags/services/TagServices";

// Alinhado com contacts/models.py PipelineAutomation.TRIGGER_TYPES
const TRIGGER_TYPES = [
  { value: "stage_enter", label: "Contato entrou no estágio" },
  { value: "stage_exit", label: "Contato saiu do estágio" },
  { value: "contact_created", label: "Contato criado" },
  { value: "deal_stagnant", label: "Deal estagnado" },
  { value: "tag_added", label: "Tag adicionada" },
  { value: "tag_removed", label: "Tag removida" },
  { value: "value_changed", label: "Valor alterado" },
  { value: "activity_completed", label: "Atividade concluída" },
];

// Alinhado com contacts/models.py PipelineAutomation.ACTION_TYPES
const ACTION_TYPES = [
  { value: "send_whatsapp", label: "Enviar mensagem WhatsApp" },
  { value: "send_email", label: "Enviar e-mail" },
  { value: "move_stage", label: "Mover para estágio" },
  { value: "assign_owner", label: "Atribuir responsável" },
  { value: "add_tag", label: "Adicionar tag" },
  { value: "remove_tag", label: "Remover tag" },
  { value: "create_activity", label: "Criar atividade" },
  { value: "toggle_bot", label: "Ativar/Desativar bot" },
];

const ACTIVITY_TYPES = [
  { value: "task", label: "Tarefa" },
  { value: "call", label: "Ligação" },
  { value: "email", label: "E-mail" },
  { value: "meeting", label: "Reunião" },
  { value: "note", label: "Nota" },
];

const getTriggerLabel = (v) => TRIGGER_TYPES.find((t) => t.value === v)?.label || v;
const getActionLabel = (v) => ACTION_TYPES.find((a) => a.value === v)?.label || v;

const triggerNeedsStage = (t) =>
  t === "stage_enter" || t === "stage_exit" || t === "deal_stagnant";

const triggerNeedsTags = (t) => t === "tag_added" || t === "tag_removed";

// BE armazena delay sempre em minutos. UI permite minutos/horas/dias.
const minutesToFriendly = (m) => {
  const min = Number(m) || 0;
  if (min === 0) return { value: 0, unit: "minutes" };
  if (min % (60 * 24) === 0) return { value: min / (60 * 24), unit: "days" };
  if (min % 60 === 0) return { value: min / 60, unit: "hours" };
  return { value: min, unit: "minutes" };
};
const friendlyToMinutes = (value, unit) => {
  const v = Number(value) || 0;
  if (unit === "days") return v * 60 * 24;
  if (unit === "hours") return v * 60;
  return v;
};
const formatDelay = (min) => {
  const { value, unit } = minutesToFriendly(min);
  if (value === 0) return "imediato";
  if (unit === "days") return `${value} ${value === 1 ? "dia" : "dias"}`;
  if (unit === "hours") return `${value}h`;
  return `${value}min`;
};

// Input numérico + select de unidade. Internamente opera em minutos:
// minutesValue (controlado) + onChange(newMinutes).
const DelayPicker = ({ minutesValue, onChange, minZero = true, hint }) => {
  const initial = minutesToFriendly(minutesValue || 0);
  const [value, setValue] = React.useState(initial.value);
  const [unit, setUnit] = React.useState(initial.unit);

  React.useEffect(() => {
    const f = minutesToFriendly(minutesValue || 0);
    setValue(f.value);
    setUnit(f.unit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minutesValue]);

  const emit = (v, u) => {
    setValue(v);
    setUnit(u);
    onChange?.(friendlyToMinutes(v, u));
  };

  return (
    <>
      <div style={{ display: "flex", gap: 8 }}>
        <input
          type="number"
          min={minZero ? "0" : "1"}
          className="crm-input"
          style={{ flex: 1 }}
          value={value}
          onChange={(e) => emit(e.target.value ? Number(e.target.value) : 0, unit)}
        />
        <select
          className="crm-select"
          style={{ flex: "0 0 130px" }}
          value={unit}
          onChange={(e) => emit(value, e.target.value)}
        >
          <option value="minutes">Minutos</option>
          <option value="hours">Horas</option>
          <option value="days">Dias</option>
        </select>
      </div>
      {hint && (
        <small style={{ color: "var(--text-secondary)", fontSize: 12, display: "block", marginTop: 4 }}>
          {hint}
        </small>
      )}
    </>
  );
};

const AutomationForm = ({ automation, pipelineId, columns, teamMembers, tags, onClose, onSaved }) => {
  const { showToast } = useCRM();
  const [name, setName] = useState(automation?.name || "");
  const [triggerType, setTriggerType] = useState(automation?.trigger_type || "stage_enter");
  const [actionType, setActionType] = useState(automation?.action_type || "send_whatsapp");
  const [triggerConfig, setTriggerConfig] = useState(automation?.trigger_config || {});
  const [actionConfig, setActionConfig] = useState(automation?.action_config || {});
  const [submitting, setSubmitting] = useState(false);

  // Unidade do atraso (delay_minutes no BE) — derivada do valor existente.
  const initialDelay = minutesToFriendly(automation?.action_config?.delay_minutes);
  const [delayValue, setDelayValue] = useState(initialDelay.value);
  const [delayUnit, setDelayUnit] = useState(initialDelay.unit);

  const handleDelayChange = (value, unit) => {
    setDelayValue(value);
    setDelayUnit(unit);
    const minutes = friendlyToMinutes(value, unit);
    setActionConfig((prev) => ({ ...prev, delay_minutes: minutes }));
  };

  // Reset configs ao trocar trigger/action para evitar lixo de campos de outro tipo
  const handleTriggerChange = (val) => {
    setTriggerType(val);
    setTriggerConfig({});
  };
  const handleActionChange = (val) => {
    setActionType(val);
    setActionConfig({});
    setDelayValue(0);
    setDelayUnit("minutes");
  };

  const updateTriggerConfig = (key, value) =>
    setTriggerConfig((prev) => ({ ...prev, [key]: value }));
  const updateActionConfig = (key, value) =>
    setActionConfig((prev) => ({ ...prev, [key]: value }));

  const validate = () => {
    if (!name.trim()) return "Informe um nome para a automação.";

    if (triggerNeedsStage(triggerType) && !triggerConfig.stage_id) {
      return "Selecione a coluna do gatilho.";
    }
    if (
      triggerType === "deal_stagnant" &&
      !triggerConfig.delay_minutes &&
      !triggerConfig.days
    ) {
      return "Informe o tempo para considerar estagnado.";
    }
    if (triggerNeedsTags(triggerType) && (!triggerConfig.tag_ids || triggerConfig.tag_ids.length === 0)) {
      return "Selecione ao menos uma tag para o gatilho.";
    }

    if (actionType === "send_whatsapp" && !(actionConfig.message || "").trim()) {
      return "Escreva a mensagem do WhatsApp.";
    }
    if (actionType === "send_email" && !(actionConfig.message || "").trim()) {
      return "Escreva o corpo do e-mail.";
    }
    if (actionType === "move_stage" && !actionConfig.stage_id) {
      return "Selecione o estágio de destino.";
    }
    if (actionType === "assign_owner" && !actionConfig.user_id) {
      return "Selecione o responsável.";
    }
    if ((actionType === "add_tag" || actionType === "remove_tag") && !actionConfig.tag_id) {
      return "Selecione a tag.";
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      showToast(err, "error");
      return;
    }
    setSubmitting(true);
    try {
      const data = {
        name: name.trim(),
        pipeline: pipelineId,
        trigger_type: triggerType,
        trigger_config: triggerConfig,
        action_type: actionType,
        action_config: actionConfig,
      };
      const saved = automation
        ? await updateAutomation(automation.id, data)
        : await createAutomation(data);
      onSaved(saved);
      showToast(automation ? "Automação atualizada!" : "Automação criada!");
      onClose();
    } catch {
      showToast("Erro ao salvar automação.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="crm-modal-backdrop" onClick={onClose}>
      <div className="crm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="crm-modal__header">
          <h3 className="crm-modal__title">{automation ? "Editar Automação" : "Nova Automação"}</h3>
          <button className="crm-drawer__close" onClick={onClose}><IconX size={20} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="crm-modal__body" style={{ maxHeight: "70vh", overflowY: "auto" }}>
            <div className="crm-form-group">
              <label className="crm-label">Nome</label>
              <input
                className="crm-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ex: Boas-vindas — 1 dia após entrar"
                required
              />
            </div>

            <h4 style={{ fontSize: 14, fontWeight: 600, marginTop: 16, marginBottom: 8 }}>Gatilho</h4>

            <div className="crm-form-group">
              <label className="crm-label">Quando disparar</label>
              <select className="crm-select" value={triggerType} onChange={(e) => handleTriggerChange(e.target.value)}>
                {TRIGGER_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>

            {triggerNeedsStage(triggerType) && (
              <div className="crm-form-group">
                <label className="crm-label">Coluna</label>
                <select
                  className="crm-select"
                  value={triggerConfig.stage_id || ""}
                  onChange={(e) => updateTriggerConfig("stage_id", e.target.value ? Number(e.target.value) : null)}
                  required
                >
                  <option value="">Selecione a coluna...</option>
                  {columns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            )}

            {triggerType === "deal_stagnant" && (
              <div className="crm-form-group">
                <label className="crm-label">Tempo parado na coluna</label>
                <DelayPicker
                  minutesValue={
                    triggerConfig.delay_minutes ??
                    (triggerConfig.days ? Number(triggerConfig.days) * 60 * 24 : 0)
                  }
                  onChange={(minutes) => {
                    setTriggerConfig((prev) => {
                      const next = { ...prev, delay_minutes: minutes };
                      delete next.days;
                      return next;
                    });
                  }}
                  minZero={false}
                  hint="Ex: 7 dias, 12 horas, 30 minutos."
                />
              </div>
            )}

            {triggerNeedsTags(triggerType) && (
              <div className="crm-form-group">
                <label className="crm-label">
                  Tags que disparam ({(triggerConfig.tag_ids || []).length} selecionada{(triggerConfig.tag_ids || []).length === 1 ? "" : "s"})
                </label>
                {tags.length === 0 ? (
                  <small style={{ color: "var(--text-secondary)", fontSize: 12 }}>
                    Nenhuma tag disponível neste pipeline. Crie tags primeiro.
                  </small>
                ) : (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, maxHeight: 160, overflowY: "auto", padding: 8, border: "1px solid var(--border-color, #e5e7eb)", borderRadius: 6 }}>
                    {tags.map((t) => {
                      const selected = (triggerConfig.tag_ids || []).includes(t.id);
                      return (
                        <button
                          type="button"
                          key={t.id}
                          onClick={() => {
                            const current = triggerConfig.tag_ids || [];
                            const next = selected
                              ? current.filter((id) => id !== t.id)
                              : [...current, t.id];
                            updateTriggerConfig("tag_ids", next);
                          }}
                          style={{
                            background: selected ? (t.color || "#3b82f6") : "transparent",
                            color: selected ? "#fff" : "var(--text-primary)",
                            border: `1px solid ${selected ? (t.color || "#3b82f6") : "var(--border-color, #d1d5db)"}`,
                            borderRadius: 999,
                            padding: "4px 10px",
                            fontSize: 12,
                            cursor: "pointer",
                          }}
                        >
                          {t.name}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            <h4 style={{ fontSize: 14, fontWeight: 600, marginTop: 16, marginBottom: 8 }}>Ação</h4>

            <div className="crm-form-group">
              <label className="crm-label">O que fazer</label>
              <select className="crm-select" value={actionType} onChange={(e) => handleActionChange(e.target.value)}>
                {ACTION_TYPES.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
              </select>
            </div>

            {(actionType === "send_whatsapp" || actionType === "send_email") && (
              <>
                {actionType === "send_email" && (
                  <div className="crm-form-group">
                    <label className="crm-label">Assunto</label>
                    <input
                      className="crm-input"
                      value={actionConfig.subject || ""}
                      onChange={(e) => updateActionConfig("subject", e.target.value)}
                      placeholder="Assunto do e-mail"
                    />
                  </div>
                )}
                <div className="crm-form-group">
                  <label className="crm-label">Mensagem</label>
                  <textarea
                    className="crm-input"
                    rows={5}
                    value={actionConfig.message || ""}
                    onChange={(e) => updateActionConfig("message", e.target.value)}
                    placeholder="Ex: Olá {nome}, vi que você se interessou..."
                    required
                  />
                  <small style={{ color: "var(--text-secondary)", fontSize: 12, display: "block", marginTop: 4 }}>
                    Variáveis disponíveis: <code>{"{nome}"}</code>, <code>{"{primeiro_nome}"}</code>, <code>{"{email}"}</code>, <code>{"{telefone}"}</code>
                    {actionType === "send_email" && <> — também válidas no Assunto.</>}
                  </small>
                </div>
                <div className="crm-form-group">
                  <label className="crm-label">Atraso após o gatilho</label>
                  <div style={{ display: "flex", gap: 8 }}>
                    <input
                      type="number"
                      min="0"
                      className="crm-input"
                      style={{ flex: 1 }}
                      value={delayValue}
                      onChange={(e) => handleDelayChange(e.target.value ? Number(e.target.value) : 0, delayUnit)}
                      placeholder="0 = imediato"
                    />
                    <select
                      className="crm-select"
                      style={{ flex: "0 0 130px" }}
                      value={delayUnit}
                      onChange={(e) => handleDelayChange(delayValue, e.target.value)}
                    >
                      <option value="minutes">Minutos</option>
                      <option value="hours">Horas</option>
                      <option value="days">Dias</option>
                    </select>
                  </div>
                  <small style={{ color: "var(--text-secondary)", fontSize: 12 }}>
                    {delayValue === 0 || !delayValue
                      ? "0 envia imediatamente."
                      : `Equivalente a ${friendlyToMinutes(delayValue, delayUnit)} minutos.`}
                  </small>
                </div>
              </>
            )}

            {actionType === "move_stage" && (
              <div className="crm-form-group">
                <label className="crm-label">Estágio de destino</label>
                <select
                  className="crm-select"
                  value={actionConfig.stage_id || ""}
                  onChange={(e) => updateActionConfig("stage_id", e.target.value ? Number(e.target.value) : null)}
                  required
                >
                  <option value="">Selecione...</option>
                  {columns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            )}

            {actionType === "assign_owner" && (
              <div className="crm-form-group">
                <label className="crm-label">Responsável</label>
                <select
                  className="crm-select"
                  value={actionConfig.user_id || ""}
                  onChange={(e) => updateActionConfig("user_id", e.target.value ? Number(e.target.value) : null)}
                  required
                >
                  <option value="">Selecione...</option>
                  {teamMembers.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.full_name || u.email || `Usuário #${u.id}`}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {(actionType === "add_tag" || actionType === "remove_tag") && (
              <div className="crm-form-group">
                <label className="crm-label">Tag</label>
                <select
                  className="crm-select"
                  value={actionConfig.tag_id || ""}
                  onChange={(e) => updateActionConfig("tag_id", e.target.value ? Number(e.target.value) : null)}
                  required
                >
                  <option value="">Selecione...</option>
                  {tags.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
            )}

            {actionType === "create_activity" && (
              <>
                <div className="crm-form-group">
                  <label className="crm-label">Título da atividade</label>
                  <input
                    className="crm-input"
                    value={actionConfig.title || ""}
                    onChange={(e) => updateActionConfig("title", e.target.value)}
                    placeholder="Ex: Ligar para o cliente"
                  />
                </div>
                <div className="crm-form-group">
                  <label className="crm-label">Tipo</label>
                  <select
                    className="crm-select"
                    value={actionConfig.activity_type || "task"}
                    onChange={(e) => updateActionConfig("activity_type", e.target.value)}
                  >
                    {ACTIVITY_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div className="crm-form-group">
                  <label className="crm-label">Agendar para</label>
                  <DelayPicker
                    minutesValue={(actionConfig.delay_hours ?? 24) * 60}
                    onChange={(minutes) => updateActionConfig("delay_hours", minutes / 60)}
                    hint="Quanto tempo após o gatilho a atividade fica agendada."
                  />
                </div>
              </>
            )}

            {actionType === "toggle_bot" && (
              <div className="crm-form-group">
                <label className="crm-label">Status do bot</label>
                <select
                  className="crm-select"
                  value={actionConfig.bot_status ?? 0}
                  onChange={(e) => updateActionConfig("bot_status", Number(e.target.value))}
                >
                  <option value={1}>Ativar bot</option>
                  <option value={0}>Desativar bot</option>
                </select>
              </div>
            )}
          </div>
          <div className="crm-modal__footer">
            <button type="button" className="crm-btn" onClick={onClose}>Cancelar</button>
            <button type="submit" className="crm-btn crm-btn--primary" disabled={submitting}>
              {submitting ? "Salvando..." : "Salvar"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const AutomationsList = () => {
  const { state, showToast } = useCRM();
  const pipelineId = state.activePipelineId;
  const columns = state.columns || [];
  const teamMembers = state.teamMembers || [];

  const [automations, setAutomations] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingAutomation, setEditingAutomation] = useState(null);

  const fetchAutomations = useCallback(async () => {
    if (!pipelineId) return;
    setLoading(true);
    try {
      const data = await getAutomations(pipelineId);
      setAutomations(data.results || data || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [pipelineId]);

  const fetchTags = useCallback(async () => {
    if (!pipelineId) return;
    try {
      const data = await getTagsByPipeline(pipelineId);
      setTags(data.results || data || []);
    } catch { /* silent */ }
  }, [pipelineId]);

  useEffect(() => { fetchAutomations(); fetchTags(); }, [fetchAutomations, fetchTags]);

  const handleToggle = async (autoId) => {
    try {
      const resp = await toggleAutomation(autoId);
      setAutomations((prev) => prev.map((a) => a.id === autoId ? { ...a, is_active: resp.is_active } : a));
    } catch {
      showToast("Erro ao alternar automação.", "error");
    }
  };

  const handleDelete = async (autoId) => {
    try {
      await deleteAutomation(autoId);
      setAutomations((prev) => prev.filter((a) => a.id !== autoId));
      showToast("Automação removida!");
    } catch {
      showToast("Erro ao remover.", "error");
    }
  };

  const handleSaved = (auto) => {
    setAutomations((prev) => {
      const exists = prev.find((a) => a.id === auto.id);
      if (exists) return prev.map((a) => a.id === auto.id ? auto : a);
      return [...prev, auto];
    });
  };

  const describeAutomation = (a) => {
    const trigger = getTriggerLabel(a.trigger_type);
    const action = getActionLabel(a.action_type);
    const cfg = a.trigger_config || {};
    const stageId = cfg.stage_id;
    const stageName = stageId ? (columns.find((c) => c.id === stageId)?.name || `#${stageId}`) : null;
    const stagePart = triggerNeedsStage(a.trigger_type) && stageName ? ` (${stageName})` : "";
    let daysPart = "";
    if (a.trigger_type === "deal_stagnant") {
      const minutes = cfg.delay_minutes ?? (cfg.days ? Number(cfg.days) * 60 * 24 : 0);
      if (minutes > 0) daysPart = ` há ${formatDelay(minutes)}`;
    }
    let tagsPart = "";
    if (triggerNeedsTags(a.trigger_type) && Array.isArray(cfg.tag_ids) && cfg.tag_ids.length > 0) {
      const tagNames = cfg.tag_ids
        .map((id) => tags.find((t) => t.id === id)?.name || `#${id}`)
        .join(", ");
      tagsPart = ` (${tagNames})`;
    }
    const delay = (a.action_config || {}).delay_minutes;
    const delayPart = delay > 0 ? ` · após ${formatDelay(delay)}` : "";
    return `${trigger}${stagePart}${daysPart}${tagsPart} → ${action}${delayPart}`;
  };

  if (loading) return <div className="crm-loading"><MoonLoader color="#3b82f6" size={24} /></div>;

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div>
          <h3 style={{ fontSize: 17, fontWeight: 600, margin: 0, color: "var(--text-primary)" }}>
            Automações do Pipeline
          </h3>
          <p style={{ fontSize: 13, color: "var(--text-secondary)", margin: "4px 0 0 0" }}>
            Configure ações automáticas (WhatsApp, e-mail, etc.) por coluna ou evento.
          </p>
        </div>
        <button className="crm-btn crm-btn--primary" onClick={() => { setEditingAutomation(null); setShowForm(true); }}>
          <IconPlus size={16} /> Nova Automação
        </button>
      </div>

      {automations.length === 0 ? (
        <div className="crm-empty">
          <span className="crm-empty__title">Nenhuma automação</span>
          <span className="crm-empty__desc">Crie automações para executar ações automaticamente quando eventos ocorrerem.</span>
        </div>
      ) : (
        automations.map((auto) => (
          <div key={auto.id} className="crm-auto__card">
            <label className="crm-auto__toggle">
              <input type="checkbox" checked={auto.is_active} onChange={() => handleToggle(auto.id)} />
              <span className="crm-auto__toggle-slider" />
            </label>

            <div className="crm-auto__info">
              <h4 className="crm-auto__name">{auto.name}</h4>
              <div className="crm-auto__trigger">{describeAutomation(auto)}</div>
              {auto.executions_count > 0 && (
                <div className="crm-auto__executions">{auto.executions_count} execuções</div>
              )}
            </div>

            <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
              <button className="crm-btn crm-btn--sm crm-btn--icon" onClick={() => { setEditingAutomation(auto); setShowForm(true); }}>
                <IconEdit size={14} />
              </button>
              <button className="crm-btn crm-btn--sm crm-btn--icon" onClick={() => handleDelete(auto.id)}>
                <IconTrash size={14} />
              </button>
            </div>
          </div>
        ))
      )}

      {showForm && (
        <AutomationForm
          automation={editingAutomation}
          pipelineId={pipelineId}
          columns={columns}
          teamMembers={teamMembers}
          tags={tags}
          onClose={() => setShowForm(false)}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
};

export default AutomationsList;
